import { useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { monthName } from '../lib/format';
import {
  activeQuick,
  monthDisabled,
  orderRange,
  previewRange,
  QUICK_RANGES,
  quickRange,
  rangeLabel,
  rangeLength,
  rangeLong,
  rangeShort,
  sameRange,
  shiftRange,
  type PeriodRange,
} from '../lib/period';
import { usePopover } from '../lib/usePopover';
import { Icon } from './ui';

interface Props {
  /** Período em exibição. */
  value: PeriodRange;
  /** Meses com dados, do mais recente para o mais antigo. */
  available: string[];
  onChange: (range: PeriodRange) => void;
}

const MONTHS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

/**
 * Seletor do período de consulta: setas que andam o período inteiro, atalhos (mensal, trimestral, semestral, anual)
 * e um calendário de meses onde se escolhe o início e o fim com dois cliques.
 * O painel é renderizado no <body>: o topo do dashboard corta o que passa da borda dele.
 */
export function PeriodPicker({ value, available, onChange }: Props) {
  const latest: string | undefined = available[0];
  const earliest: string | undefined = available[available.length - 1];

  const [viewYear, setViewYear] = useState(() => Number(value.to.slice(0, 4)));
  const [anchor, setAnchor] = useState<string | null>(null);
  const [hover, setHover] = useState<string | null>(null);
  const popoverId = useId();
  const {
    open,
    position,
    triggerRef,
    popoverRef,
    toggle,
    close: closePopover,
  } = usePopover({
    width: 360,
    estimatedHeight: 480,
    onOpen: () => {
      setViewYear(Number(value.to.slice(0, 4)));
      setAnchor(null);
      setHover(null);
    },
    onClose: () => {
      setAnchor(null);
      setHover(null);
    },
  });

  function apply(range: PeriodRange) {
    closePopover(true);
    if (!sameRange(range, value)) onChange(range);
  }

  function pick(month: string) {
    if (!anchor) {
      setAnchor(month);
      setHover(month);
      return;
    }
    apply(orderRange(anchor, month));
  }

  const single = value.from === value.to;
  const canPrev = earliest !== undefined && value.from > earliest;
  const canNext = latest !== undefined && value.to < latest;
  const canPrevYear = earliest === undefined || viewYear > Number(earliest.slice(0, 4));
  const canNextYear = latest === undefined || viewYear < Number(latest.slice(0, 4));
  const active = activeQuick(value, latest);
  const preview = previewRange(value, anchor, hover);
  const previewMonths = rangeLength(preview);

  return (
    <div className="period-picker" role="group" aria-label="Período de consulta">
      <button
        type="button"
        className="icon-btn"
        disabled={!canPrev}
        onClick={() => onChange(shiftRange(value, -1))}
        aria-label={single ? 'Mês anterior' : 'Período anterior'}
      >
        <Icon name="chevronLeft" />
      </button>

      <button
        ref={triggerRef}
        type="button"
        className="period-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={toggle}
      >
        <span className="period-trigger__label">
          <Icon name="calendar" size={16} />
          <span>{rangeLabel(value)}</span>
        </span>
        <Icon name="chevronDown" size={16} />
      </button>

      <button
        type="button"
        className="icon-btn"
        disabled={!canNext}
        onClick={() => onChange(shiftRange(value, 1))}
        aria-label={single ? 'Próximo mês' : 'Próximo período'}
      >
        <Icon name="chevronRight" />
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={popoverRef}
            id={popoverId}
            className="period-pop"
            role="dialog"
            aria-label="Escolher período"
            tabIndex={-1}
            style={{ top: position.top, left: position.left, width: position.width, maxHeight: position.maxHeight }}
          >
            <div className="stack" style={{ gap: 8 }}>
              <p className="label">Atalhos</p>
              <div className="period-quick">
                {QUICK_RANGES.map((q) => {
                  const range = quickRange(q.months, latest ?? value.to);
                  return (
                    <button key={q.key} type="button" aria-pressed={active === q.key} onClick={() => apply(range)}>
                      <span>{q.label}</span>
                      <small>{rangeShort(range)}</small>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="stack" style={{ gap: 8 }}>
              <div className="period-cal__head">
                <p className="label">Escolher no calendário</p>
                <div className="period-cal__nav">
                  <button
                    type="button"
                    className="icon-btn icon-btn--sm"
                    disabled={!canPrevYear}
                    onClick={() => setViewYear((y) => y - 1)}
                    aria-label="Ano anterior"
                  >
                    <Icon name="chevronLeft" size={15} />
                  </button>
                  <span className="period-cal__year num" aria-live="polite">
                    {viewYear}
                  </span>
                  <button
                    type="button"
                    className="icon-btn icon-btn--sm"
                    disabled={!canNextYear}
                    onClick={() => setViewYear((y) => y + 1)}
                    aria-label="Próximo ano"
                  >
                    <Icon name="chevronRight" size={15} />
                  </button>
                </div>
              </div>

              <div className="period-cal__grid" onMouseLeave={() => anchor && setHover(anchor)}>
                {MONTHS.map((mm) => {
                  const month = `${viewYear}-${mm}`;
                  const isStart = month === preview.from;
                  const isEnd = month === preview.to;
                  const cls = [
                    'period-cal__month',
                    month >= preview.from && month <= preview.to && 'is-in',
                    isStart && 'is-start',
                    isEnd && 'is-end',
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <button
                      key={month}
                      type="button"
                      className={cls}
                      disabled={monthDisabled(month, anchor, { earliest, latest })}
                      aria-pressed={isStart || isEnd}
                      aria-label={`${monthName(month)} de ${viewYear}`}
                      onClick={() => pick(month)}
                      onMouseEnter={() => anchor && setHover(month)}
                      onFocus={() => anchor && setHover(month)}
                    >
                      {monthName(month).slice(0, 3)}
                    </button>
                  );
                })}
              </div>

              <p className="period-cal__summary" aria-live="polite">
                <b>{rangeLong(preview)}</b>
                <span className="t-caption"> · {previewMonths === 1 ? '1 mês' : `${previewMonths} meses`}</span>
              </p>
              <p className="t-caption">
                {anchor
                  ? 'Agora escolha o mês final. Clique no mesmo mês para ver só ele.'
                  : 'Clique no mês inicial e depois no final. Clique duas vezes no mesmo mês para ver só ele.'}
              </p>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
